import { observer } from 'mobx-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { MobileBackButton, MobileSearchIcon, ModalCloseIcon } from "../../../../assets/icons/common/common-icons";
import { ActionLockedIconWithTooltip } from '../../../../common/components/ActionLocked/ActionLockedIconWithTooltip';
import { LimitationModal } from '../../../../common/components/LimitationModal/LimitationModal';
import { AppLoader } from '../../../../common/components/Loaders/AppLoader';
import { colors } from '../../../../common/styles/theme.styles';
import { ActivationChecklistPane } from "../../../activation/components/checklist-pane/ChecklistPane";
import { useStore } from "../../../app/data/root.store";
import { AppHeader } from "../../../layout/components/Header/Header";
import { RightSidePushMenuWrapper } from "../../../layout/components/Layout.styles";
import { MobileBackButtonWrapper } from "../../../layout/components/MobileLayout.styles";
import { HeaderTitle, ProfileMobileHeader } from "../../../user/components/Profile.styles";
import { VaultUploadIcon } from '../../assets/icons';
import { MAX_VAULT_FILE_SIZE_MB, SUPPORTED_FILE_FORMATS, SUPPORTED_FILE_FORMATS_MAP } from '../Vault.const';
import { VaultHeader } from "../VaultHeader/VaultHeader";
import { VaultButtonBlack } from '../VaultHeader/VaultHeader.styles';
import { VaultMainTable } from "../VaultMainTable/VaultMainTable";
import { VaultMetadataPanel } from "../VaultMetadataPanel/VaultMetadataPanel";
import { VaultMobileView } from "../VaultMobileView/VaultMobileView";
import { VaultModals } from '../VaultModals/VaultModals';
import { VaultSearch } from "../VaultSearch/VaultSearch";
import { VaultTooltipsBar } from '../VaultTooltipsBar/VaultTooltipsBar';
import { VaultUpload } from '../VaultUpload/VaultUpload';
import { MainContent, PageContent, PageWrapper, VaultSearchButton, VaultUploadButtonText } from "./Vault.styles";
import { useHistory, useLocation } from 'react-router-dom';

export const VaultPage = observer(() => {
    const { uiStore, userStore, vaultStore, billingStore } = useStore();
    const [uploadModalState, setUploadModalState] = useState({ isOpen: false, createItemsState: false });
    const [showMobileSearchBox, setShowMobileSearchBox] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const fileInputRef = useRef();

    const location = useLocation();
    const history = useHistory();

    const isMetadataPanelOpen = vaultStore.isMetadataPanelOpen;
    const shouldShowPanelInLayout = uiStore.isVaultMetadataPanelModalThreshold && isMetadataPanelOpen;
    const hasPagination = vaultStore.meta.totalPages > 1;



    const handleFileChange = async (e) => {
        setIsLoading(true);
        try {
            const files = e.target.files;
            if (files && files.length > 0) {
                const supportedFormats = Object.values(SUPPORTED_FILE_FORMATS_MAP);
                const maxSize = (MAX_VAULT_FILE_SIZE_MB * 1024 * 1024);
                const mappedFiles = Array.from(files).reduce((acc, file) => {
                    if (supportedFormats.includes(file.type) && (file.size <= maxSize)) {
                        acc.accepted.push(file);
                    } else {
                        acc.rejected.push(file);
                    }
                    return acc;
                }, { accepted: [], rejected: [] });
                await vaultStore.upload(mappedFiles.accepted, mappedFiles.rejected, { directUpload: true });
            }
        } catch (error) {
            console.error('Error uploading documents:', error);
        } finally {
            setIsLoading(false);
        }

        e.target.value = '';
    };

    const openUpload = useCallback((files = null, createItemsState = false) => {
        if (uiStore.isDesktopView) {
            setUploadModalState({ isOpen: true, createItemsState, files });
        } else if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    }, [uiStore.isDesktopView, setUploadModalState, fileInputRef]);

    useEffect(() => {
        vaultStore.resetAndRefetchFiles();
        return () => {
            vaultStore.clearVaultFilters();
        }
    }, [vaultStore]);

    useEffect(() => {
        if (location.state?.openUpload) {
            openUpload(null, true);

            const newState = { ...location.state };
            delete newState.openUpload;
            history.replace({ ...location, state: newState });
        }

    }, [location, history, openUpload]);

    const handleUploadClick = () => {
        if (userStore.isActionBlockedByRole('addVaultFile')) {
            userStore.setShowLimitedSharedAccess(true);
            return;
        }

        if (userStore.isDemoMode) {
            billingStore.setShowPricingTableModal(true);
        } else if (userStore.isReadOnlyMode) {
            uiStore.setShowDemoLimitationModal(true);
        } else {
            openUpload();
        }
    }

    const closeUploadModal = () => setUploadModalState(prev => ({ ...prev, isOpen: false }))

    return (
        <>
            {isLoading && <AppLoader backdrop absolute />}
            {uiStore.isDesktopView ? (
                <AppHeader />
            ) : (
                <ProfileMobileHeader isHeaderFixed={uiStore.isMobileHeaderFixed} isSearchOpen={showMobileSearchBox} style={{ backgroundColor: '#fff', paddingRight: '16px' }}>
                    {!showMobileSearchBox ? (
                        <>
                            <MobileBackButtonWrapper onClick={() => { uiStore.setIsAppMobileMenuOpen(true) }} fontColor={colors.darkGray1}>
                                <MobileBackButton />
                            </MobileBackButtonWrapper>
                            <HeaderTitle style={{ color: colors.darkGray1 }}>Vault</HeaderTitle>
                            <VaultButtonBlack
                                style={{ fontSize: '14px', height: '36px', padding: '0 12px', marginLeft: 'auto' }}
                                onClick={() => fileInputRef.current.click()}
                            >
                                <VaultUploadIcon />
                                <VaultUploadButtonText>
                                    Upload files
                                    {userStore.isActionBlockedByRole('addVaultFile') && <ActionLockedIconWithTooltip iconColor={'#FFF'} isRoleBlocked={true} marginLeft={'0px'} />}
                                </VaultUploadButtonText>
                            </VaultButtonBlack>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept={SUPPORTED_FILE_FORMATS.join(',')}
                                multiple
                                style={{ display: 'none' }}
                                onChange={handleFileChange}
                            />
                            <VaultSearchButton onClick={() => setShowMobileSearchBox(true)}>
                                <MobileSearchIcon />
                            </VaultSearchButton>
                        </>
                    ) : (
                        <>
                            <div style={{ flex: 1, marginRight: '8px' }}>
                                <VaultSearch isMobile={true} hideTitleOnMobile={true} />
                            </div>
                            <VaultSearchButton onClick={() => setShowMobileSearchBox(false)}>
                                <ModalCloseIcon />
                            </VaultSearchButton>
                        </>
                    )}
                </ProfileMobileHeader>
            )}
            <RightSidePushMenuWrapper>
                {userStore.isViewingOwnPortfolio && userStore.isGetStarted && <ActivationChecklistPane />}
            </RightSidePushMenuWrapper>

            {uiStore.isDesktopView ? (
                <PageWrapper style={{ paddingBottom: 0, paddingTop: 0 }}>
                    <VaultHeader onUploadClick={handleUploadClick} />
                    <PageContent withPanel={shouldShowPanelInLayout}>
                        <MainContent>
                            <VaultMainTable openUpload={openUpload} />
                            {!hasPagination && (
                                <VaultTooltipsBar position="bottom" />
                            )}
                        </MainContent>
                        {shouldShowPanelInLayout && <VaultMetadataPanel />}
                    </PageContent>
                </PageWrapper>
            ) : (
                <VaultMobileView />
            )}

            {!uiStore.isVaultMetadataPanelModalThreshold && isMetadataPanelOpen && <VaultMetadataPanel />}
            {uploadModalState?.isOpen && <VaultUpload onClose={closeUploadModal} createItemsState={uploadModalState?.createItemsState} files={uploadModalState?.files} />}
            <VaultModals onFailed={openUpload} createItemsState={uploadModalState?.createItemsState} />
            {uiStore.isShowDemoLimitationModal && <LimitationModal modalType={'readOnlyPortfolio'} onStartMyPortfolio={() => { billingStore.setShowPricingTableModal(true) }} onClose={() => { uiStore.setShowDemoLimitationModal(false) }} />}
        </>
    );
});